import React, { useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserCircle, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABEL = {
  super_admin: "Super Administrator", agency_staff: "Agency Staff",
  client_owner: "Client Owner", client_staff: "Client Staff",
};

function timeZones() {
  try {
    if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
  } catch {}
  return ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
    "Europe/London", "Europe/Paris", "Asia/Kolkata", "Asia/Tokyo", "Australia/Sydney"];
}

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const zones = useMemo(timeZones, []);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    display_name: user?.display_name || "",
    company: user?.company || "",
    time_zone: user?.time_zone || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await updateProfile(form);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Account" title="Your Profile"
        subtitle="Your Display Name is used across Vozelto, including the Executive Dashboard greeting." />

      <div className="max-w-2xl card-matte p-6" data-testid="profile-card">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-full bg-[#6B3DFF]/15 flex items-center justify-center"><UserCircle size={28} className="brand-text" weight="fill" /></div>
          <div>
            <div className="font-head text-lg font-semibold">{form.display_name || form.first_name || user?.email}</div>
            <div className="text-xs text-[#7a7a7a]">{ROLE_LABEL[user?.role] || "User"}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="label-caps">First Name</Label>
            <Input data-testid="profile-first-name" value={form.first_name} onChange={set("first_name")} className="bg-[#10101A] border-white/10 mt-1" />
          </div>
          <div>
            <Label className="label-caps">Last Name</Label>
            <Input data-testid="profile-last-name" value={form.last_name} onChange={set("last_name")} className="bg-[#10101A] border-white/10 mt-1" />
          </div>
          <div>
            <Label className="label-caps">Display Name</Label>
            <Input data-testid="profile-display-name" value={form.display_name} onChange={set("display_name")} placeholder="Shown in greetings" className="bg-[#10101A] border-white/10 mt-1" />
          </div>
          <div>
            <Label className="label-caps">Company</Label>
            <Input data-testid="profile-company" value={form.company} onChange={set("company")} className="bg-[#10101A] border-white/10 mt-1" />
          </div>
          <div>
            <Label className="label-caps">Email</Label>
            <Input data-testid="profile-email" value={user?.email || ""} readOnly disabled className="bg-[#10101A] border-white/10 mt-1 opacity-70" />
          </div>
          <div>
            <Label className="label-caps">Role</Label>
            <Input data-testid="profile-role" value={ROLE_LABEL[user?.role] || "User"} readOnly disabled className="bg-[#10101A] border-white/10 mt-1 opacity-70" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-caps">Time Zone</Label>
            <select data-testid="profile-time-zone" value={form.time_zone} onChange={set("time_zone")} className="w-full bg-[#10101A] border border-white/10 rounded-lg px-3 py-2 text-sm mt-1">
              <option value="">Select time zone…</option>
              {zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <Button data-testid="profile-save" onClick={save} disabled={busy} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white">
            <FloppyDisk size={16} className="mr-1.5" /> {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
